import { ModalLayoutProps } from '.';
import ModalLayout from './ModalLayout';
import Slider from 'react-slick';
import './ImageGallery.module.css';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { useState, useEffect } from 'react';

interface ImageGalleryModalProps extends ModalLayoutProps {
    images: string[];
    zIndex?: number;
}

const ImageGalleryModal = ({ visible, onClose, images, zIndex = 10000 }: ImageGalleryModalProps) => {
    const [currentSlide, setCurrentSlide] = useState(0);
    const [sliderRef, setSliderRef] = useState<Slider | null>(null);

    // Enhanced settings for better mobile experience
    const settings = {
        dots: false, // Disable default dots since we have custom ones
        infinite: false,
        speed: 300,
        slidesToShow: 1,
        slidesToScroll: 1,
        swipe: true,
        touchMove: true,
        swipeToSlide: true,
        touchThreshold: 10,
        responsive: [
            {
                breakpoint: 768,
                settings: {
                    dots: false, // Disable default dots on mobile too
                    arrows: false, // Hide arrows on mobile for cleaner look
                    swipe: true,
                    touchMove: true,
                    swipeToSlide: true,
                    touchThreshold: 5, // More sensitive on mobile
                },
            },
        ],
        beforeChange: (current: number, next: number) => {
            setCurrentSlide(next);
        },
        afterChange: (current: number) => {
            setCurrentSlide(current);
        },
    };

    // Reset current slide when modal opens
    useEffect(() => {
        if (visible) {
            setCurrentSlide(0);
            if (sliderRef) {
                sliderRef.slickGoTo(0);
            }
        }
    }, [visible, sliderRef]);

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            onClose();
        } else if (e.key === 'ArrowLeft' && sliderRef) {
            sliderRef.slickPrev();
        } else if (e.key === 'ArrowRight' && sliderRef) {
            sliderRef.slickNext();
        }
    };

    return (
        <ModalLayout visible={visible} onClose={onClose} showCloseIcon={false} showShadow={false} zIndex={zIndex}>
            <div className="fixed inset-0 bg-black/90" style={{ zIndex: zIndex }} onClick={handleBackdropClick} onKeyDown={handleKeyDown} tabIndex={0}>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-[95vw] sm:max-w-[90vw]">
                    <div className="max-h-[95vh] sm:max-h-[90vh] relative" onClick={(e) => e.stopPropagation()}>
                        {/* Close Button - Mobile Optimized */}
                        <div
                            className="absolute right-2 sm:right-0 sm:-top-12 cursor-pointer bg-white/90 rounded-full p-2 sm:p-3 hover:bg-white transition-colors z-10"
                            style={{
                                zIndex: zIndex + 1,
                                top: '70px',
                            }}
                            onClick={onClose}
                        >
                            <CloseRoundedIcon className="text-lg sm:text-xl" />
                        </div>

                        {/* Image Counter - Mobile Optimized */}
                        <div
                            className="absolute left-2 sm:left-0 sm:-top-12 bg-black/70 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full text-sm sm:text-base font-medium z-10"
                            style={{
                                zIndex: zIndex + 1,
                                top: '70px',
                            }}
                        >
                            {currentSlide + 1} / {images.length}
                        </div>

                        {/* Main Slider */}
                        <Slider ref={(slider) => setSliderRef(slider)} {...settings} className="image-gallery-slider">
                            {images.map((image: string, index: number) => (
                                <div key={index} className="outline-none">
                                    <div className="h-[85vh] sm:h-[80vh] flex justify-center items-center px-2 sm:px-[20px]">
                                        <img
                                            src={image}
                                            className="max-h-[85vh] sm:max-h-[80vh] max-w-full object-contain rounded-lg"
                                            alt={`Property image ${index + 1}`}
                                            style={{
                                                maxHeight: '85vh',
                                                maxWidth: '100%',
                                                userSelect: 'none',
                                                WebkitUserSelect: 'none',
                                                WebkitTouchCallout: 'none',
                                            }}
                                            draggable={false}
                                        />
                                    </div>
                                </div>
                            ))}
                        </Slider>

                        {/* Mobile Navigation Dots - Enhanced */}
                        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10">
                            <div className="flex space-x-2 max-w-[80vw] overflow-x-auto scrollbar-hide px-4">
                                {images.map((_, index) => (
                                    <button
                                        key={index}
                                        onClick={() => sliderRef?.slickGoTo(index)}
                                        className={`flex-shrink-0 w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-200 ${
                                            index === currentSlide ? 'bg-white scale-125' : 'bg-white/50 hover:bg-white/75'
                                        }`}
                                        aria-label={`Go to image ${index + 1}`}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ModalLayout>
    );
};

export default ImageGalleryModal;
